import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionGroup,
  Card,
  FileUpload,
  Flex,
  Illustration,
  LinearProgressIndicator,
  LoadingIndicator,
  Text,
  toast,
  type FileObjectType,
} from "@jsq/ds/core";
import { useDocumentIngestionsQuery } from "#graphql/supergraphHooks";
import { useCoverageRefresh } from "#hooks/useCoverageRefresh";
import { useFayAuthGateArmed, useReportFayAuthFailure } from "#hooks/useFayAuthGate";
import { useStartDocumentIngestion } from "#hooks/useStartDocumentIngestion";
import { isFayAuthFailure } from "#utils/fayAuthErrors";
import { BatchCard } from "../BatchCard";
import { Slideout } from "../Slideout";
import * as styles from "./styles_vanilla.css";
import type { IngestionState } from "#graphql/supergraphTypes";

const INGESTIONS_PAGE_SIZE = 20;
const INGESTIONS_POLL_MS = 2000;
// Batches older than this collapse into the "Uploaded over a week ago" accordion
// so the rail stays focused on current work without dropping history.
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export type UploadRailProps = {
  /** Arena the ingestion list and the upload write are scoped to. Undefined
   *  until the host app resolves it, which skips the listing query. */
  arenaId: number | undefined;
};

export const UploadRail: React.FC<UploadRailProps> = ({ arenaId }) => {
  const refreshCoverage = useCoverageRefresh();
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const [selectedIngestionId, setSelectedIngestionId] = useState<string | null>(null);
  const { state, upload, reset } = useStartDocumentIngestion(arenaId);
  // The listing is arena-scoped server-side (fay requires arenaId); skip until a
  // valid arena resolves so we never issue the required arg as NaN.
  const { data, loading, error, stopPolling } = useDocumentIngestionsQuery({
    variables: { arenaId: arenaId!, limit: INGESTIONS_PAGE_SIZE },
    pollInterval: INGESTIONS_POLL_MS,
    fetchPolicy: "cache-and-network",
    skip: arenaId === undefined,
  });

  // A terminal fay auth failure (kill-switch/permission off while open, or a
  // logged-out session) won't recover, so stop polling and report up to the page
  // gate so the whole page swaps to a generic error page.
  const reportAuthFailure = useReportFayAuthFailure();
  // Only hand the auth case off to the page gate when it's armed (provider
  // mounted and the release flag on); otherwise the inline line below stays the
  // only surface.
  const authGateArmed = useFayAuthGateArmed();
  useEffect(() => {
    if (error && isFayAuthFailure(error)) {
      stopPolling();
      reportAuthFailure(error);
    }
  }, [error, stopPolling, reportAuthFailure]);

  const isBusy =
    state.phase === "presigning" || state.phase === "uploading" || state.phase === "starting";

  const handleFileSelected = async (files: FileObjectType[]) => {
    const accepted = files.filter((f) => f.status === "accepted").map((f) => f.file);
    if (accepted.length === 0) {
      return;
    }
    setAcceptedFiles(accepted);
    try {
      await upload(accepted);
      const noun = accepted.length === 1 ? "document" : "documents";
      toast.success(`Started ingestion for ${accepted.length} ${noun}`);
      setAcceptedFiles([]);
      reset();
    } catch {
      /* error surfaced via serverRejectedFiles */
    }
  };

  const serverRejectedFiles =
    state.phase === "error" && state.error && acceptedFiles.length > 0
      ? acceptedFiles.map((file) => ({ file, error: state.error as string }))
      : [];

  const ingestions = data?.documentIngestions.ingestions ?? [];
  // Hide two kinds of batch from the rail: ones whose documents were all removed
  // (documentCount 0), and COMPLETED ones — once approved a batch drops off the
  // rail just like a removed document, leaving only Classifying and Ready-for-
  // review cards. The unfiltered list still backs the slideout's state lookup
  // below, so a batch open at the moment it empties or completes keeps resolving
  // to its real state (the confirm screen's "No documents to review" empty state,
  // or the read-only completed summary) instead of stranding on the live tree.
  const visibleIngestions = ingestions.filter(
    (i) => i.documentCount > 0 && i.state !== "COMPLETED",
  );
  // Partition the rail by age: batches received within the last week render
  // inline; older ones collapse into an accordion at the bottom. An unparseable
  // receivedAt is treated as recent (shown inline) rather than hidden away.
  const weekAgo = Date.now() - ONE_WEEK_MS;
  const isOlderThanAWeek = (receivedAt: string): boolean => {
    const received = new Date(receivedAt).getTime();
    return !Number.isNaN(received) && received < weekAgo;
  };
  const recentIngestions = visibleIngestions.filter((i) => !isOlderThanAWeek(i.receivedAt));
  const olderIngestions = visibleIngestions.filter((i) => isOlderThanAWeek(i.receivedAt));

  // Drive the slideout's live-tree-vs-confirm-screen choice off the polled list
  // state, so it flips to the confirm screen as soon as fay moves the ingestion
  // to AWAITING_CONFIRMATION, and to the read-only summary once it's COMPLETED.
  // The polled list is capped at INGESTIONS_PAGE_SIZE, so a still-open ingestion
  // can drop out of it once enough newer uploads arrive. When the lookup misses,
  // fall back to the last-known state for the selected ingestion rather than null
  // — a null would flip an open slideout back to the live tree and re-subscribe.
  // The retained state is cleared when the drawer closes or a different one opens.
  const lastSelectedStateRef = useRef<{ id: string; state: IngestionState } | null>(null);
  const polledSelectedState =
    ingestions.find((i) => i.id === selectedIngestionId)?.state ?? null;
  if (selectedIngestionId === null) {
    lastSelectedStateRef.current = null;
  } else if (polledSelectedState !== null) {
    lastSelectedStateRef.current = { id: selectedIngestionId, state: polledSelectedState };
  }
  const selectedIngestionState =
    polledSelectedState ??
    (lastSelectedStateRef.current?.id === selectedIngestionId
      ? lastSelectedStateRef.current.state
      : null);

  return (
    <>
      <Card title="Incoming documents" variant="white" isFullWidth padding="lg">
        <Flex flexDirection="column" gap={16}>
          <div className={styles.uploadContainer}>
            <FileUpload
              uploadMessage="Upload your file(s) here"
              isFullWidth
              size="large"
              canAllowMultiple
              acceptedFileTypes={[
                "application/pdf",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/vnd.ms-excel",
                "application/vnd.ms-excel.sheet.binary.macroEnabled.12",
                "text/csv",
                // Browsers frequently report an empty/octet-stream MIME for
                // .xlsb, so match on extension too (react-dropzone accepts
                // either).
                ".xlsb",
              ]}
              // Suppress the DS's auto-generated "Supported formats: ..."
              // label — its formatter doesn't special-case `.xlsb` or the
              // macroEnabled Excel MIME. We overlay the canonical copy below,
              // positioned inside the drop zone.
              displayAcceptedFileTypes={false}
              isDisabled={isBusy}
              isLoading={isBusy}
              shouldClearFiles
              // Upload progress and failure both surface through `state`, so the
              // promise is intentionally unawaited here.
              onFileSelected={(files) => void handleFileSelected(files)}
              serverRejectedFiles={serverRejectedFiles}
            />
            <Text variant="secondary-sm" className={styles.supportedFormats}>
              Supported formats: PDF, Excel (.xlsx, .xlsb)
            </Text>
          </div>
          {state.phase === "uploading" && (
            <Flex flexDirection="column" gap="4px">
              <Text variant="regular-sm">Uploading… {state.bytesPercent}%</Text>
              <LinearProgressIndicator progressPercentage={state.bytesPercent} />
            </Flex>
          )}
          {state.phase === "starting" && <Text variant="regular-sm">Starting ingestion…</Text>}

          {/* Auth failures drive the whole-page error swap, so suppress the
              inline line for those; keep it for transient/non-auth errors (e.g. a
              500 or network blip) that don't gate the page. */}
          {error && !(authGateArmed && isFayAuthFailure(error)) && (
            <Text variant="regular-sm">Failed to load ingestions: {error.message}</Text>
          )}
          {loading && !data && (
            <Flex justifyContent="center" alignItems="center" style={{ minHeight: 80 }}>
              <LoadingIndicator />
            </Flex>
          )}
          {!error && visibleIngestions.length === 0 && !(loading && !data) && (
            // Compact empty state: EmptyState renders a full-size hero
            // illustration that overwhelms the narrow rail, so we compose a
            // smaller one inline with a width-constrained Illustration.
            <Flex
              flexDirection="column"
              alignItems="center"
              gap={8}
              style={{ paddingBlock: 24, textAlign: "center" }}
            >
              <Illustration name="upload-documents" alt="" width={96} />
              <Text variant="strong-md">No documents yet</Text>
              <Text variant="secondary-sm">Upload one or more files above to get started.</Text>
            </Flex>
          )}
          {visibleIngestions.length > 0 && (
            <Flex flexDirection="column" gap={8}>
              {recentIngestions.length > 0 && (
                // Scrollable region so a long list never pushes the page; the
                // upload zone above stays pinned. Newest ingestions are first.
                <div style={{ maxHeight: 480, overflowY: "auto" }}>
                  <Flex flexDirection="column" gap={8}>
                    {recentIngestions.map((ingestion) => (
                      <BatchCard
                        key={ingestion.id}
                        ingestion={ingestion}
                        onSelect={setSelectedIngestionId}
                      />
                    ))}
                  </Flex>
                </div>
              )}
              {olderIngestions.length > 0 && (
                <AccordionGroup>
                  <Accordion title="Uploaded over a week ago" startExpanded={false}>
                    <Flex flexDirection="column" gap={8}>
                      {olderIngestions.map((ingestion) => (
                        <BatchCard
                          key={ingestion.id}
                          ingestion={ingestion}
                          onSelect={setSelectedIngestionId}
                        />
                      ))}
                    </Flex>
                  </Accordion>
                </AccordionGroup>
              )}
            </Flex>
          )}
        </Flex>
      </Card>
      <Slideout
        ingestionId={selectedIngestionId}
        ingestionState={selectedIngestionState}
        onClose={() => setSelectedIngestionId(null)}
        onApproved={() => {
          // Auto-close the (opaque) drawer so the newly-created deliverable
          // packages become visible, then refresh the sibling coverage grid so
          // they appear immediately instead of on the next 15s poll tick. The
          // success toast still confirms what was approved.
          setSelectedIngestionId(null);
          refreshCoverage();
        }}
        arenaId={arenaId}
      />
    </>
  );
};
